<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useEmailInbox } from 'dashboard/composables/useEmailInbox';
import EmailInboxAPI from 'dashboard/api/aceleres/emailInbox';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import EmailListItem from './EmailListItem.vue';
import EmailInboxEmptyState from './EmailInboxEmptyState.vue';
import EmailRecipientChips from './EmailRecipientChips.vue';
import EmailSnoozeDialog from './EmailSnoozeDialog.vue';
import EmailThreadView from './EmailThreadView.vue';
import LabelTagIcon from 'dashboard/components-next/label/LabelTagIcon.vue';

// Aceleres — cliente de e-mail da Caixa de Entrada, com a hierarquia visual
// inspirada no Gmail e ações reais ligadas ao endpoint da inbox.
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const {
  unreadCount: emailUnreadCount,
  inboxes,
  labels,
  threads,
  totalCount,
  perPage,
  currentPage,
  filters,
  isLoadingInboxes,
  isLoadingThreads,
  hasLoadedThreads,
  loadError,
  selectedThreadIds,
  isBulkActionLoading,
  alertInboxes,
  fetchInboxes,
  fetchLabels,
  fetchThreads,
  fetchUnreadCount,
  bulkMarkRead,
  bulkMarkUnread,
  goToPage,
  setListMounted,
  toggleThreadSelected,
  setSelectedThreadIds,
  clearSelection,
  toggleThreadStar,
} = useEmailInbox();

const search = ref(filters.q);
const searchFiltersOpen = ref(false);
const mobileNavigationOpen = ref(false);
const moreNavigationOpen = ref(false);
const moreActionsOpen = ref(false);
const composeOpen = ref(false);
const isComposing = ref(false);
const draftId = ref(null);
const draftFiles = ref([]);
const pendingFiles = ref([]);
const isSavingDraft = ref(false);
const snoozeOpen = ref(false);
const snoozeAnchor = ref(null);
const toRecipients = ref(null);
const ccRecipients = ref(null);
const bccRecipients = ref(null);
const showCopyRecipients = ref(false);
const labelOpen = ref(false);
const labelName = ref('');
const labelColor = ref('#16834b');
const labelSelection = ref([]);
const composeForm = ref({
  inboxId: '',
  to: '',
  cc: '',
  bcc: '',
  subject: '',
  content: '',
});
let searchTimer = null;
let draftTimer = null;
let refreshTimer = null;
let draftSaveChain = Promise.resolve();
let knownResurfaced = new Set();
let mountedAt = Date.now();

const activeThreadId = computed(() =>
  route.name === 'inbox_view_email_thread' ? Number(route.params.id) : null
);
const hasManyInboxes = computed(() => inboxes.value.length > 1);
const hasNoInboxes = computed(
  () => !isLoadingInboxes.value && inboxes.value.length === 0
);
const selectedCount = computed(() => selectedThreadIds.value.size);
const allVisibleSelected = computed(
  () =>
    threads.value.length > 0 &&
    threads.value.every(thread => selectedThreadIds.value.has(thread.id))
);
const someVisibleSelected = computed(
  () =>
    !allVisibleSelected.value &&
    threads.value.some(thread => selectedThreadIds.value.has(thread.id))
);
const hasPreviousPage = computed(() => currentPage.value > 1);
const hasNextPage = computed(
  () => currentPage.value * perPage.value < totalCount.value
);
const rangeStart = computed(() =>
  totalCount.value ? (currentPage.value - 1) * perPage.value + 1 : 0
);
const rangeEnd = computed(() =>
  Math.min(currentPage.value * perPage.value, totalCount.value)
);
const hasActiveFilter = computed(
  () =>
    filters.folder !== 'inbox' ||
    filters.category !== 'primary' ||
    Boolean(filters.q) ||
    Boolean(filters.inboxId) ||
    Boolean(filters.label)
);
const showEmptyState = computed(
  () =>
    hasLoadedThreads.value &&
    !isLoadingThreads.value &&
    !loadError.value &&
    threads.value.length === 0
);
const selectedInboxId = computed(
  () => composeForm.value.inboxId || inboxes.value[0]?.id || ''
);

const folders = computed(() => [
  {
    key: 'inbox',
    label: t('INBOX.EMAIL.FOLDER_INBOX'),
    icon: 'i-lucide-inbox',
    count: emailUnreadCount.value,
  },
  {
    key: 'starred',
    label: t('INBOX.EMAIL.FOLDER_STARRED'),
    icon: 'i-lucide-star',
  },
  {
    key: 'snoozed',
    label: t('INBOX.EMAIL.FOLDER_SNOOZED'),
    icon: 'i-lucide-clock-3',
  },
  {
    key: 'sent',
    label: t('INBOX.EMAIL.FOLDER_SENT'),
    icon: 'i-lucide-send',
  },
  {
    key: 'drafts',
    label: t('INBOX.EMAIL.FOLDER_DRAFTS'),
    icon: 'i-lucide-file-pen-line',
  },
]);

const moreFolders = computed(() => [
  {
    key: 'unread',
    label: t('INBOX.EMAIL.FOLDER_UNREAD'),
    icon: 'i-lucide-mail',
  },
  {
    key: 'read',
    label: t('INBOX.EMAIL.FOLDER_READ'),
    icon: 'i-lucide-mail-open',
  },
  {
    key: 'spam',
    label: t('INBOX.EMAIL.FOLDER_SPAM'),
    icon: 'i-lucide-shield-alert',
  },
  {
    key: 'trash',
    label: t('INBOX.EMAIL.FOLDER_TRASH'),
    icon: 'i-lucide-trash-2',
  },
  {
    key: 'archived',
    label: t('INBOX.EMAIL.FOLDER_ARCHIVED'),
    icon: 'i-lucide-archive',
  },
]);

const categories = computed(() => [
  {
    key: 'primary',
    label: t('INBOX.EMAIL.CATEGORY_PRIMARY'),
    icon: 'i-lucide-inbox',
  },
  {
    key: 'promotions',
    label: t('INBOX.EMAIL.CATEGORY_PROMOTIONS'),
    icon: 'i-lucide-tag',
  },
  {
    key: 'social',
    label: t('INBOX.EMAIL.CATEGORY_SOCIAL'),
    icon: 'i-lucide-users',
  },
  {
    key: 'notifications',
    label: t('INBOX.EMAIL.CATEGORY_NOTIFICATIONS'),
    icon: 'i-lucide-bell',
  },
]);

const showList = () => {
  if (activeThreadId.value)
    router.push({ name: 'inbox_view', query: { tab: 'emails' } });
};

const setFolder = folder => {
  showList();
  clearSelection();
  moreActionsOpen.value = false;
  filters.folder = folder;
  filters.label = '';
  filters.category = 'primary';
  filters.status = folder === 'unread' || folder === 'read' ? folder : 'all';
  mobileNavigationOpen.value = false;
  searchFiltersOpen.value = false;
  fetchThreads();
};

const setLabel = label => {
  showList();
  clearSelection();
  filters.folder = 'label';
  filters.status = 'all';
  filters.category = 'primary';
  filters.label = label;
  mobileNavigationOpen.value = false;
  searchFiltersOpen.value = false;
  fetchThreads();
};

const setCategory = category => {
  showList();
  clearSelection();
  filters.folder = 'inbox';
  filters.status = 'all';
  filters.label = '';
  filters.category = category;
  searchFiltersOpen.value = false;
  fetchThreads();
};

const setInbox = event => {
  showList();
  clearSelection();
  filters.inboxId = event.target.value ? Number(event.target.value) : null;
  searchFiltersOpen.value = false;
  fetchThreads();
};

const clearEmailFilters = () => {
  search.value = '';
  filters.inboxId = null;
  setFolder('inbox');
};

const toggleSelectAll = () => {
  if (filters.folder === 'drafts') return;
  if (allVisibleSelected.value) {
    const ids = new Set(selectedThreadIds.value);
    threads.value.forEach(thread => ids.delete(thread.id));
    setSelectedThreadIds(ids);
    return;
  }

  setSelectedThreadIds([
    ...selectedThreadIds.value,
    ...threads.value.map(thread => thread.id),
  ]);
};

const refreshList = async () => {
  clearSelection();
  moreActionsOpen.value = false;
  await fetchInboxes();
  await fetchLabels();
  await fetchThreads({ page: currentPage.value });
};

const refreshResurfaced = async () => {
  if (
    composeOpen.value ||
    filters.folder !== 'inbox' ||
    currentPage.value !== 1
  )
    return;
  await fetchThreads();
  await fetchUnreadCount();
};

const saveDraft = async () => {
  if (!composeForm.value.inboxId || isComposing.value) return true;
  if (
    !draftId.value &&
    !composeForm.value.to &&
    !composeForm.value.cc &&
    !composeForm.value.bcc &&
    !composeForm.value.subject &&
    !composeForm.value.content &&
    !pendingFiles.value.length
  )
    return true;
  const snapshot = { ...composeForm.value, files: [...pendingFiles.value] };
  pendingFiles.value = [];
  isSavingDraft.value = true;
  draftSaveChain = draftSaveChain
    .catch(() => {})
    .then(async () => {
      const { data } = await EmailInboxAPI.saveDraft({
        draftId: draftId.value,
        inboxId: snapshot.inboxId,
        to: snapshot.to,
        cc: snapshot.cc,
        bcc: snapshot.bcc,
        subject: snapshot.subject,
        content: snapshot.content,
        files: snapshot.files,
      });
      draftId.value = data.data.draft_id;
      draftFiles.value = data.data.files || [];
    });
  try {
    await draftSaveChain;
    return true;
  } catch (error) {
    pendingFiles.value = [...snapshot.files, ...pendingFiles.value];
    useAlert(error?.response?.data?.error || t('INBOX.EMAIL.DRAFT_ERROR'));
    return false;
  } finally {
    isSavingDraft.value = false;
  }
};

const addDraftFiles = async event => {
  pendingFiles.value = [
    ...pendingFiles.value,
    ...Array.from(event.target.files || []),
  ];
  event.target.value = '';
  clearTimeout(draftTimer);
  await saveDraft();
};

const removeDraftFile = async file => {
  if (!draftId.value) return;
  try {
    const { data } = await EmailInboxAPI.deleteDraftFile(
      draftId.value,
      file.id
    );
    draftFiles.value = data.data.files || [];
  } catch {
    useAlert(t('INBOX.EMAIL.DRAFT_ERROR'));
  }
};

const markSelectedRead = async () => {
  try {
    await bulkMarkRead([...selectedThreadIds.value]);
    useAlert(t('INBOX.EMAIL.SELECTED_MARKED_READ'));
  } catch {
    useAlert(t('INBOX.EMAIL.BULK_ACTION_ERROR'));
  }
};

const markSelectedUnread = async () => {
  try {
    await bulkMarkUnread([...selectedThreadIds.value]);
    useAlert(t('INBOX.EMAIL.SELECTED_MARKED_UNREAD'));
  } catch {
    useAlert(t('INBOX.EMAIL.BULK_ACTION_ERROR'));
  }
};

const moveSelected = async folder => {
  try {
    await Promise.all(
      [...selectedThreadIds.value].map(id =>
        EmailInboxAPI.moveFolder(id, folder)
      )
    );
    clearSelection();
    await fetchThreads();
    await fetchUnreadCount();
    useAlert(t('INBOX.EMAIL.FOLDER_MOVED'));
  } catch (error) {
    await fetchThreads();
    useAlert(error?.response?.data?.error || t('INBOX.EMAIL.FOLDER_ERROR'));
  }
};

const toggleStar = async thread => {
  try {
    await toggleThreadStar(thread.id);
  } catch {
    useAlert(t('INBOX.EMAIL.STAR_ERROR'));
  }
};

const openThread = async thread => {
  if (thread.draft_id) {
    await draftSaveChain.catch(() => {});
    draftId.value = thread.draft_id;
    draftFiles.value = thread.files || [];
    pendingFiles.value = [];
    composeForm.value = {
      inboxId: thread.inbox_id,
      to: thread.to,
      cc: thread.cc || '',
      bcc: thread.bcc || '',
      subject: thread.subject,
      content: thread.content,
    };
    composeOpen.value = true;
    showCopyRecipients.value = Boolean(thread.cc || thread.bcc);
    return;
  }
  mobileNavigationOpen.value = false;
  router.push({
    name: 'inbox_view_email_thread',
    params: { id: thread.id },
    query: { tab: 'emails' },
  });
};

const openCompose = async () => {
  await draftSaveChain.catch(() => {});
  draftId.value = null;
  draftFiles.value = [];
  pendingFiles.value = [];
  composeForm.value = {
    inboxId: selectedInboxId.value,
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    content: '',
  };
  composeOpen.value = true;
  showCopyRecipients.value = false;
  mobileNavigationOpen.value = false;
};

const closeCompose = async () => {
  if (isComposing.value) return;
  clearTimeout(draftTimer);
  const saving = saveDraft();
  composeOpen.value = false;
  await saving;
  if (filters.folder === 'drafts') fetchThreads();
};

const discardDraft = async () => {
  clearTimeout(draftTimer);
  try {
    await draftSaveChain.catch(() => {});
    if (draftId.value) await EmailInboxAPI.deleteDraft(draftId.value);
    draftId.value = null;
    composeOpen.value = false;
    if (filters.folder === 'drafts') fetchThreads();
  } catch {
    useAlert(t('INBOX.EMAIL.DRAFT_ERROR'));
  }
};

const sendCompose = async () => {
  if (isComposing.value) return;
  if (
    ![toRecipients.value, ccRecipients.value, bccRecipients.value].every(
      field => !field || field.commit()
    )
  ) {
    useAlert(t('INBOX.EMAIL.INVALID_RECIPIENT'));
    return;
  }
  clearTimeout(draftTimer);
  await draftSaveChain.catch(() => {});
  if (pendingFiles.value.length && !(await saveDraft())) return;
  isComposing.value = true;
  try {
    const { data } = await EmailInboxAPI.compose({
      inboxId: composeForm.value.inboxId,
      to: composeForm.value.to,
      cc: composeForm.value.cc,
      bcc: composeForm.value.bcc,
      subject: composeForm.value.subject,
      content: composeForm.value.content,
      draftId: draftId.value,
    });
    composeOpen.value = false;
    useAlert(t('INBOX.EMAIL.COMPOSE_SENT'));
    await fetchThreads({ page: 1 });
    if (data?.thread_id) openThread({ id: data.thread_id });
  } catch (error) {
    useAlert(error?.response?.data?.error || t('INBOX.EMAIL.COMPOSE_ERROR'));
  } finally {
    isComposing.value = false;
  }
};

const snoozeSelected = async until => {
  if (!selectedCount.value) return;
  try {
    await Promise.all(
      [...selectedThreadIds.value].map(id => EmailInboxAPI.snooze(id, until))
    );
    snoozeOpen.value = false;
    clearSelection();
    await fetchThreads();
    useAlert(t('INBOX.EMAIL.SNOOZED'));
  } catch (error) {
    useAlert(error?.response?.data?.error || t('INBOX.EMAIL.SNOOZE_ERROR'));
  }
};

const openSnooze = event => {
  const { left, top, bottom } = event.currentTarget.getBoundingClientRect();
  snoozeAnchor.value = { left, top, bottom };
  snoozeOpen.value = true;
};

const openLabels = () => {
  labelSelection.value =
    selectedCount.value === 1
      ? [
          ...(threads.value.find(item => selectedThreadIds.value.has(item.id))
            ?.labels || []),
        ]
      : [];
  labelOpen.value = true;
};

const saveLabels = async () => {
  try {
    await Promise.all(
      [...selectedThreadIds.value].map(id =>
        EmailInboxAPI.updateLabels(id, labelSelection.value)
      )
    );
    labelOpen.value = false;
    clearSelection();
    await fetchLabels();
    await fetchThreads();
  } catch (error) {
    useAlert(error?.response?.data?.error || t('INBOX.EMAIL.LABEL_ERROR'));
  }
};

const createLabel = async () => {
  if (!labelName.value.trim()) return;
  try {
    const { data } = await EmailInboxAPI.createLabel(
      labelName.value.trim(),
      labelColor.value
    );
    if (selectedCount.value)
      labelSelection.value = [...labelSelection.value, data.title];
    labelName.value = '';
    await fetchLabels();
  } catch (error) {
    useAlert(error?.response?.data?.error || t('INBOX.EMAIL.LABEL_ERROR'));
  }
};

const closePopups = event => {
  if (event.key === 'Escape') {
    searchFiltersOpen.value = false;
    moreNavigationOpen.value = false;
    moreActionsOpen.value = false;
    snoozeOpen.value = false;
    labelOpen.value = false;
    if (composeOpen.value) closeCompose();
    return;
  }
  if (event.type !== 'pointerdown' || !event.target?.closest) return;
  if (!event.target.closest('[data-email-popup="search"]'))
    searchFiltersOpen.value = false;
  if (!event.target.closest('[data-email-popup="more"]'))
    moreNavigationOpen.value = false;
  if (!event.target.closest('[data-email-popup="actions"]'))
    moreActionsOpen.value = false;
};

watch(search, value => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    if (filters.q === value) return;
    clearSelection();
    filters.q = value;
    fetchThreads();
  }, 400);
});

watch(
  composeForm,
  () => {
    if (!composeOpen.value) return;
    clearTimeout(draftTimer);
    draftTimer = setTimeout(saveDraft, 700);
  },
  { deep: true }
);

watch(threads, currentThreads => {
  const current = new Set(
    currentThreads
      .filter(item => item.resurfaced_at)
      .map(item => `${item.id}:${item.resurfaced_at}`)
  );
  if (
    currentThreads.some(
      item =>
        item.resurfaced_at &&
        !knownResurfaced.has(`${item.id}:${item.resurfaced_at}`) &&
        new Date(item.resurfaced_at).getTime() > mountedAt
    )
  ) {
    useAlert(t('INBOX.EMAIL.SNOOZE_RETURNED'));
  }
  knownResurfaced = current;
});

onMounted(() => {
  mountedAt = Date.now();
  setListMounted(true);
  fetchInboxes();
  fetchLabels();
  fetchThreads();
  document.addEventListener('pointerdown', closePopups);
  document.addEventListener('keydown', closePopups);
  refreshTimer = setInterval(refreshResurfaced, 60000);
});

onUnmounted(() => {
  setListMounted(false);
  clearSelection();
  clearTimeout(searchTimer);
  clearTimeout(draftTimer);
  clearInterval(refreshTimer);
  document.removeEventListener('pointerdown', closePopups);
  document.removeEventListener('keydown', closePopups);
});
</script>

<template>
  <div
    class="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-[#f6f8fc] text-[#202124]"
  >
    <div class="relative flex min-h-0 flex-1">
      <button
        v-if="mobileNavigationOpen"
        type="button"
        class="absolute inset-0 z-20 bg-black/30 md:hidden"
        :aria-label="t('INBOX.EMAIL.CLOSE_NAVIGATION')"
        @click="mobileNavigationOpen = false"
      />
      <aside
        class="absolute inset-y-0 left-0 z-30 flex w-72 shrink-0 -translate-x-full flex-col overflow-y-auto border-r border-[#e0e3e7] bg-white px-3 py-4 shadow-xl transition-transform md:relative md:z-0 md:w-60 md:translate-x-0 md:shadow-none lg:w-64"
        :class="{ 'translate-x-0': mobileNavigationOpen }"
        :aria-label="t('INBOX.EMAIL.MAIL_NAVIGATION')"
      >
        <div class="flex items-center justify-between px-1 md:hidden">
          <span class="text-sm font-semibold text-[#202124]">{{
            t('INBOX.EMAIL.MAIL_NAVIGATION')
          }}</span>
          <button
            type="button"
            class="inline-flex size-8 items-center justify-center rounded-full text-[#5f6368] hover:bg-[#f1f3f4]"
            :aria-label="t('INBOX.EMAIL.CLOSE_NAVIGATION')"
            @click="mobileNavigationOpen = false"
          >
            <span class="i-lucide-x size-4" aria-hidden="true" />
          </button>
        </div>
        <button
          type="button"
          class="mt-3 flex h-12 items-center gap-3 rounded-2xl bg-[#15603e] px-5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#104b31] md:mt-1"
          @click="openCompose"
        >
          <span class="i-lucide-pencil size-5" aria-hidden="true" />{{
            t('INBOX.EMAIL.COMPOSE')
          }}
        </button>
        <nav class="mt-5 flex flex-col gap-1">
          <button
            v-for="folder in folders"
            :key="folder.key"
            type="button"
            class="flex min-h-9 items-center gap-3 rounded-r-full px-4 text-left text-[13px] transition-colors"
            :class="
              filters.folder === folder.key && !filters.label
                ? 'bg-[#e6f4ea] font-semibold text-[#15603e]'
                : 'text-[#3c4043] hover:bg-[#f1f3f4]'
            "
            :aria-current="
              filters.folder === folder.key && !filters.label
                ? 'page'
                : undefined
            "
            @click="setFolder(folder.key)"
          >
            <span
              class="size-[18px] shrink-0"
              :class="[folder.icon]"
              aria-hidden="true"
            /><span class="min-w-0 flex-1 truncate">{{ folder.label }}</span
            ><span v-if="folder.count" class="text-xs font-semibold">{{
              folder.count
            }}</span>
          </button>
          <button
            type="button"
            class="flex min-h-9 items-center gap-3 rounded-r-full px-4 text-left text-[13px] text-[#3c4043] hover:bg-[#f1f3f4]"
            :aria-expanded="moreNavigationOpen"
            data-email-popup="more"
            @click="moreNavigationOpen = !moreNavigationOpen"
          >
            <span
              class="size-[18px] shrink-0 transition-transform"
              :class="
                moreNavigationOpen
                  ? 'i-lucide-chevron-up'
                  : 'i-lucide-chevron-down'
              "
              aria-hidden="true"
            /><span>{{ t('INBOX.EMAIL.MORE') }}</span>
          </button>
          <div
            v-if="moreNavigationOpen"
            data-email-popup="more"
            class="ml-8 flex flex-col gap-1"
          >
            <button
              v-for="folder in moreFolders"
              :key="folder.key"
              type="button"
              class="flex items-center gap-2 rounded-r-full px-3 py-1.5 text-left text-[13px] text-[#3c4043] hover:bg-[#f1f3f4]"
              :class="{
                'bg-[#e6f4ea] font-semibold text-[#15603e]':
                  filters.folder === folder.key,
              }"
              @click="setFolder(folder.key)"
            >
              <span class="size-4" :class="folder.icon" aria-hidden="true" />
              {{ folder.label }}
            </button>
          </div>
        </nav>
        <div class="mt-5 border-t border-[#e0e3e7] pt-4">
          <div class="flex items-center justify-between px-3">
            <span
              class="text-xs font-semibold uppercase tracking-wide text-[#5f6368]"
              >{{ t('INBOX.EMAIL.LABELS') }}</span
            ><button
              type="button"
              class="inline-flex size-7 items-center justify-center rounded-full text-[#5f6368] hover:bg-[#f1f3f4]"
              :aria-label="t('INBOX.EMAIL.ADD_LABEL')"
              :title="t('INBOX.EMAIL.ADD_LABEL')"
              @click="labelOpen = true"
            >
              <span class="i-lucide-plus size-4" aria-hidden="true" />
            </button>
          </div>
          <div v-if="labels.length" class="mt-2 flex flex-col gap-1">
            <button
              v-for="label in labels"
              :key="label.id"
              type="button"
              class="flex min-h-8 items-center gap-2 rounded-r-full px-3 text-left text-[13px] text-[#3c4043] hover:bg-[#f1f3f4]"
              :class="
                filters.label === label.title
                  ? 'bg-[#e6f4ea] font-semibold text-[#15603e]'
                  : ''
              "
              @click="setLabel(label.title)"
            >
              <LabelTagIcon :color="label.color" :size="14" />
              <span class="min-w-0 flex-1 truncate">{{ label.title }}</span>
              <span v-if="label.count" class="text-xs text-[#5f6368]">
                {{ label.count }}
              </span>
            </button>
          </div>
          <p v-else class="px-3 py-2 text-xs text-[#80868b]">
            {{ t('INBOX.EMAIL.NO_LABELS') }}
          </p>
        </div>
        <div
          v-if="hasManyInboxes"
          class="mt-auto border-t border-[#e0e3e7] pt-4"
        >
          <label
            class="px-3 text-xs font-semibold uppercase tracking-wide text-[#5f6368]"
            for="email-inbox-account"
            >{{ t('INBOX.EMAIL.ACCOUNTS') }}</label
          ><select
            id="email-inbox-account"
            class="mt-2 h-9 w-full rounded-lg border border-[#dadce0] bg-white px-2 text-xs text-[#3c4043] outline-none focus:border-[#16834b]"
            :value="filters.inboxId || ''"
            @change="setInbox"
          >
            <option value="">{{ t('INBOX.EMAIL.ALL_ACCOUNTS') }}</option>
            <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
              {{ inbox.email }}
            </option>
          </select>
        </div>
      </aside>

      <main
        class="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-white"
      >
        <EmailThreadView
          v-if="activeThreadId"
          @open-navigation="mobileNavigationOpen = true"
        />
        <template v-else>
          <header
            class="flex shrink-0 items-center gap-3 border-b border-[#e0e3e7] px-4 py-3 md:px-6 md:py-4"
          >
            <button
              type="button"
              class="inline-flex size-9 shrink-0 items-center justify-center rounded-full text-[#5f6368] hover:bg-[#f1f3f4] md:hidden"
              :aria-label="t('INBOX.EMAIL.OPEN_NAVIGATION')"
              @click="mobileNavigationOpen = true"
            >
              <span class="i-lucide-menu size-5" aria-hidden="true" />
            </button>
            <div class="flex min-w-0 items-center gap-2 text-[#202124]">
              <span
                class="i-lucide-mail size-5 shrink-0 text-[#15603e] md:size-6"
                aria-hidden="true"
              />
              <h1
                class="truncate text-lg font-semibold tracking-tight md:text-xl"
              >
                {{ t('INBOX.EMAIL.HEADER_TITLE') }}
              </h1>
            </div>
            <div class="ml-auto hidden items-center gap-1 md:flex">
              <span
                class="rounded-full bg-[#e6f4ea] px-3 py-1 text-xs font-medium text-[#15603e]"
                >{{ t('INBOX.EMAIL.ACTIVE_STATUS') }}</span
              >
            </div>
          </header>

          <div
            class="flex shrink-0 items-center gap-2 border-b border-[#e0e3e7] bg-white px-3 py-3 md:px-5 md:py-4"
          >
            <label
              class="hidden h-10 min-w-0 flex-1 items-center gap-2 rounded-lg border border-n-weak bg-n-surface-1 px-3.5 text-n-slate-10 transition-colors focus-within:border-n-teal-8 md:flex"
            >
              <span
                class="i-lucide-search size-4 shrink-0"
                aria-hidden="true"
              />
              <input
                v-model="search"
                type="text"
                inputmode="search"
                autocomplete="off"
                class="min-w-0 flex-1 appearance-none !border-0 !bg-transparent !p-0 text-sm text-n-slate-12 !shadow-none !outline-none !ring-0 placeholder:text-n-slate-9"
                :placeholder="t('INBOX.EMAIL.SEARCH_PLACEHOLDER')"
                :aria-label="t('INBOX.EMAIL.SEARCH_PLACEHOLDER')"
              />
              <button
                v-if="search"
                type="button"
                class="inline-flex size-7 shrink-0 items-center justify-center rounded-full text-n-slate-10 hover:bg-n-alpha-2"
                :aria-label="t('INBOX.EMAIL.CLEAR_SEARCH')"
                @click="search = ''"
              >
                <span class="i-lucide-x size-4" aria-hidden="true" />
              </button>
            </label>
            <button
              type="button"
              class="hidden size-9 shrink-0 items-center justify-center rounded-full text-[#5f6368] hover:bg-[#e8eaed] md:inline-flex"
              :class="searchFiltersOpen ? 'bg-[#e6f4ea] text-[#15603e]' : ''"
              :aria-label="t('INBOX.EMAIL.SEARCH_FILTERS')"
              :aria-expanded="searchFiltersOpen"
              data-email-popup="search"
              @click="searchFiltersOpen = !searchFiltersOpen"
            >
              <span
                class="i-lucide-list-filter size-[18px]"
                aria-hidden="true"
              />
            </button>
            <label
              class="flex h-10 min-w-0 flex-1 items-center gap-2 rounded-lg border border-n-weak bg-n-surface-1 px-3 text-n-slate-10 transition-colors focus-within:border-n-teal-8 md:hidden"
            >
              <span
                class="i-lucide-search size-4 shrink-0"
                aria-hidden="true"
              />
              <input
                v-model="search"
                type="text"
                inputmode="search"
                autocomplete="off"
                class="min-w-0 flex-1 appearance-none !border-0 !bg-transparent !p-0 text-sm text-n-slate-12 !shadow-none !outline-none !ring-0 placeholder:text-n-slate-9"
                :placeholder="t('INBOX.EMAIL.SEARCH_PLACEHOLDER')"
                :aria-label="t('INBOX.EMAIL.SEARCH_PLACEHOLDER')"
              />
            </label>
            <button
              type="button"
              class="inline-flex size-8 shrink-0 items-center justify-center rounded-full text-n-slate-10 hover:bg-n-alpha-2 md:hidden"
              :class="searchFiltersOpen ? 'bg-[#e6f4ea] text-[#15603e]' : ''"
              :aria-label="t('INBOX.EMAIL.SEARCH_FILTERS')"
              :aria-expanded="searchFiltersOpen"
              data-email-popup="search"
              @click="searchFiltersOpen = !searchFiltersOpen"
            >
              <span
                class="i-lucide-list-filter size-[18px]"
                aria-hidden="true"
              />
            </button>
            <button
              v-if="search"
              type="button"
              class="inline-flex size-8 shrink-0 items-center justify-center rounded-full text-n-slate-10 hover:bg-n-alpha-2 md:hidden"
              :aria-label="t('INBOX.EMAIL.CLEAR_SEARCH')"
              @click="search = ''"
            >
              <span class="i-lucide-x size-4" aria-hidden="true" />
            </button>
          </div>

          <div
            v-if="searchFiltersOpen"
            data-email-popup="search"
            class="z-40 border-b border-[#dfe3eb] bg-white px-3 py-3 shadow-[0_8px_18px_rgba(60,64,67,0.12)] md:absolute md:left-3 md:right-3 md:top-[7.25rem] md:rounded-2xl md:border md:px-4 md:py-4"
          >
            <div class="flex items-center justify-between gap-3">
              <div>
                <p class="text-sm font-semibold text-[#202124]">
                  {{ t('INBOX.EMAIL.SEARCH_FILTERS') }}
                </p>
                <p class="mt-0.5 text-xs text-[#80868b]">
                  {{ t('INBOX.EMAIL.SEARCH_FILTERS_DESCRIPTION') }}
                </p>
              </div>
              <button
                type="button"
                class="inline-flex size-8 items-center justify-center rounded-full text-[#5f6368] hover:bg-[#f1f3f4]"
                :aria-label="t('INBOX.EMAIL.CLOSE_SEARCH_FILTERS')"
                @click="searchFiltersOpen = false"
              >
                <span class="i-lucide-x size-4" aria-hidden="true" />
              </button>
            </div>
            <div class="mt-3 grid gap-3 md:grid-cols-2">
              <div>
                <p
                  class="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#80868b]"
                >
                  {{ t('INBOX.EMAIL.FOLDERS') }}
                </p>
                <div class="flex flex-wrap gap-1.5">
                  <button
                    v-for="folder in [...folders, ...moreFolders]"
                    :key="`search-${folder.key}`"
                    type="button"
                    class="inline-flex min-h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors"
                    :class="
                      filters.folder === folder.key && !filters.label
                        ? 'border-[#16834b] bg-[#e6f4ea] text-[#15603e]'
                        : 'border-[#dfe3eb] text-[#5f6368] hover:bg-[#f5faf6]'
                    "
                    @click="setFolder(folder.key)"
                  >
                    <span
                      class="size-3.5"
                      :class="folder.icon"
                      aria-hidden="true"
                    />
                    {{ folder.label }}
                  </button>
                </div>
              </div>
              <div>
                <p
                  class="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#80868b]"
                >
                  {{ t('INBOX.EMAIL.CATEGORIES') }}
                </p>
                <div class="flex flex-wrap gap-1.5">
                  <button
                    v-for="category in categories"
                    :key="`search-category-${category.key}`"
                    type="button"
                    class="inline-flex min-h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors"
                    :class="
                      filters.category === category.key && !filters.label
                        ? 'border-[#16834b] bg-[#e6f4ea] text-[#15603e]'
                        : 'border-[#dfe3eb] text-[#5f6368] hover:bg-[#f5faf6]'
                    "
                    @click="setCategory(category.key)"
                  >
                    <span
                      class="size-3.5"
                      :class="category.icon"
                      aria-hidden="true"
                    />
                    {{ category.label }}
                  </button>
                </div>
              </div>
            </div>
            <div
              class="mt-3 flex flex-col gap-2 border-t border-[#eef0f3] pt-3 sm:flex-row sm:items-center"
            >
              <label
                class="flex h-10 w-full min-w-0 items-center gap-2 rounded-lg border border-[#dfe3eb] px-3 transition-colors hover:border-[#c7cdd6] focus-within:border-[#15603e] focus-within:ring-2 focus-within:ring-[#15603e]/10 sm:w-64 sm:flex-none"
              >
                <span
                  class="i-lucide-mail-check size-4 shrink-0 text-[#80868b]"
                  aria-hidden="true"
                />
                <span class="sr-only">{{ t('INBOX.EMAIL.ACCOUNTS') }}</span>
                <select
                  class="h-full min-w-0 flex-1 appearance-none truncate !border-0 !bg-none !bg-transparent !px-0 !py-0 text-xs text-[#3c4043] !shadow-none !outline-none !ring-0 focus:!border-0 focus:!shadow-none focus:!outline-none focus:!ring-0"
                  :value="filters.inboxId || ''"
                  @change="setInbox"
                >
                  <option value="">{{ t('INBOX.EMAIL.ALL_ACCOUNTS') }}</option>
                  <option
                    v-for="inbox in inboxes"
                    :key="inbox.id"
                    :value="inbox.id"
                  >
                    {{ inbox.email }}
                  </option>
                </select>
                <span
                  class="i-lucide-chevron-down size-3.5 shrink-0 text-[#80868b]"
                  aria-hidden="true"
                />
              </label>
              <button
                type="button"
                class="inline-flex min-h-10 items-center justify-center rounded-xl px-3 text-xs font-semibold text-[#15603e] hover:bg-[#e6f4ea]"
                @click="clearEmailFilters"
              >
                {{ t('INBOX.EMAIL.CLEAR_FILTERS') }}
              </button>
            </div>
          </div>

          <div
            v-for="inbox in alertInboxes"
            :key="`alert-${inbox.id}`"
            class="mx-3 mt-2 flex items-start gap-2 rounded-lg border border-n-amber-6 bg-n-amber-2 px-3 py-2 text-xs text-n-amber-11 md:mx-5"
            role="alert"
          >
            <span class="i-lucide-triangle-alert mt-0.5 size-4 shrink-0" />
            <span>{{
              inbox.reauthorization_required
                ? t('INBOX.EMAIL.AUTH_FAILED', { email: inbox.email })
                : t('INBOX.EMAIL.IMAP_DISABLED', { email: inbox.email })
            }}</span>
          </div>

          <div
            class="hidden min-h-14 shrink-0 flex-wrap items-center justify-between gap-2 border-b border-[#e0e3e7] px-4 py-2 md:flex lg:px-5"
          >
            <div class="flex flex-wrap items-center gap-1">
              <label
                class="inline-flex size-9 cursor-pointer items-center justify-center rounded-md hover:bg-[#f1f3f4]"
                :title="t('INBOX.EMAIL.SELECT_ALL')"
                ><input
                  type="checkbox"
                  class="size-4 cursor-pointer accent-[#15603e]"
                  :checked="allVisibleSelected"
                  :indeterminate="someVisibleSelected"
                  :disabled="filters.folder === 'drafts' || !threads.length"
                  :aria-label="t('INBOX.EMAIL.SELECT_ALL')"
                  @change="toggleSelectAll" /></label
              ><button
                type="button"
                class="inline-flex size-9 items-center justify-center rounded-full text-[#5f6368] hover:bg-[#f1f3f4] disabled:opacity-40"
                :aria-label="t('INBOX.EMAIL.REFRESH')"
                :title="t('INBOX.EMAIL.REFRESH')"
                :disabled="isLoadingThreads || isBulkActionLoading"
                @click="refreshList"
              >
                <span
                  class="i-lucide-refresh-cw size-[18px]"
                  aria-hidden="true"
                />
              </button>
              <div data-email-popup="actions" class="relative">
                <button
                  type="button"
                  class="inline-flex size-9 items-center justify-center rounded-full text-[#5f6368] hover:bg-[#f1f3f4]"
                  :aria-expanded="moreActionsOpen"
                  :aria-label="t('INBOX.EMAIL.MORE_ACTIONS')"
                  :title="t('INBOX.EMAIL.MORE_ACTIONS')"
                  @click="moreActionsOpen = !moreActionsOpen"
                >
                  <span
                    class="i-lucide-ellipsis-vertical size-[18px]"
                    aria-hidden="true"
                  /></button
                ><span
                  v-if="moreActionsOpen"
                  class="absolute left-0 top-10 z-20 flex min-w-44 flex-col rounded-lg border border-[#dadce0] bg-white p-1 text-left shadow-lg"
                  ><span class="rounded px-3 py-2 text-xs text-[#5f6368]">{{
                    t('INBOX.EMAIL.MORE_ACTIONS_HINT')
                  }}</span
                  ><button
                    type="button"
                    class="rounded px-3 py-2 text-left text-xs text-[#3c4043] hover:bg-[#f1f3f4]"
                    @click="refreshList"
                  >
                    {{ t('INBOX.EMAIL.REFRESH') }}</button
                  ><button
                    type="button"
                    class="rounded px-3 py-2 text-left text-xs text-[#3c4043] hover:bg-[#f1f3f4]"
                    @click="setFolder('inbox')"
                  >
                    {{ t('INBOX.EMAIL.CLEAR_FILTERS') }}
                  </button></span
                >
              </div>
              <template v-if="selectedCount">
                <span class="ml-2 text-xs font-medium text-[#5f6368]">{{
                  t('INBOX.EMAIL.SELECTED_COUNT', { count: selectedCount })
                }}</span
                ><button
                  type="button"
                  class="inline-flex size-9 items-center justify-center rounded-full text-[#5f6368] hover:bg-[#f1f3f4]"
                  :aria-label="t('INBOX.EMAIL.MARK_READ')"
                  :title="t('INBOX.EMAIL.MARK_READ')"
                  :disabled="isBulkActionLoading"
                  @click="markSelectedRead"
                >
                  <span
                    class="i-lucide-mail-open size-[18px]"
                    aria-hidden="true"
                  /></button
                ><button
                  type="button"
                  class="inline-flex size-9 items-center justify-center rounded-full text-[#5f6368] hover:bg-[#f1f3f4]"
                  :aria-label="t('INBOX.EMAIL.MARK_UNREAD')"
                  :title="t('INBOX.EMAIL.MARK_UNREAD')"
                  :disabled="isBulkActionLoading"
                  @click="markSelectedUnread"
                >
                  <span
                    class="i-lucide-mail size-[18px]"
                    aria-hidden="true"
                  /></button
                ><button
                  type="button"
                  class="inline-flex size-9 items-center justify-center rounded-full text-[#5f6368] hover:bg-[#f1f3f4]"
                  :aria-label="t('INBOX.EMAIL.SNOOZE')"
                  :title="t('INBOX.EMAIL.SNOOZE')"
                  @click="openSnooze"
                >
                  <span
                    class="i-lucide-clock-3 size-[18px]"
                    aria-hidden="true"
                  /></button
                ><button
                  type="button"
                  class="inline-flex size-9 items-center justify-center rounded-full text-[#5f6368] hover:bg-[#f1f3f4]"
                  :aria-label="t('INBOX.EMAIL.LABELS')"
                  :title="t('INBOX.EMAIL.LABELS')"
                  @click="openLabels"
                >
                  <LabelTagIcon :size="18" />
                </button>
                <button
                  v-for="action in ['trash', 'spam', 'archived'].includes(
                    filters.folder
                  )
                    ? [
                        {
                          key: 'inbox',
                          icon: 'i-lucide-inbox',
                          label: t('INBOX.EMAIL.RESTORE'),
                        },
                      ]
                    : [
                        {
                          key: 'archived',
                          icon: 'i-lucide-archive',
                          label: t('INBOX.EMAIL.ARCHIVE'),
                        },
                        {
                          key: 'spam',
                          icon: 'i-lucide-shield-alert',
                          label: t('INBOX.EMAIL.MARK_SPAM'),
                        },
                        {
                          key: 'trash',
                          icon: 'i-lucide-trash-2',
                          label: t('INBOX.EMAIL.MOVE_TRASH'),
                        },
                      ]"
                  :key="action.key"
                  type="button"
                  class="inline-flex size-9 items-center justify-center rounded-full text-[#5f6368] hover:bg-[#f1f3f4]"
                  :aria-label="action.label"
                  :title="action.label"
                  @click="moveSelected(action.key)"
                >
                  <span
                    :class="action.icon"
                    class="size-[18px]"
                    aria-hidden="true"
                  /></button
                ><button
                  type="button"
                  class="inline-flex size-9 items-center justify-center rounded-full text-[#5f6368] hover:bg-[#f1f3f4]"
                  :aria-label="t('INBOX.EMAIL.CLEAR_SELECTION')"
                  :title="t('INBOX.EMAIL.CLEAR_SELECTION')"
                  @click="clearSelection"
                >
                  <span class="i-lucide-x size-[18px]" aria-hidden="true" />
                </button>
              </template>
            </div>
            <div class="flex items-center gap-1 text-xs text-[#5f6368]">
              <span
                >{{ rangeStart }}–{{ rangeEnd }} {{ t('INBOX.EMAIL.OF') }}
                {{ totalCount }}</span
              ><button
                type="button"
                class="inline-flex size-8 items-center justify-center rounded-full hover:bg-[#f1f3f4] disabled:opacity-30"
                :aria-label="t('INBOX.EMAIL.PREVIOUS_PAGE')"
                :disabled="!hasPreviousPage || isLoadingThreads"
                @click="goToPage(currentPage - 1)"
              >
                <span
                  class="i-lucide-chevron-left size-4"
                  aria-hidden="true"
                /></button
              ><button
                type="button"
                class="inline-flex size-8 items-center justify-center rounded-full hover:bg-[#f1f3f4] disabled:opacity-30"
                :aria-label="t('INBOX.EMAIL.NEXT_PAGE')"
                :disabled="!hasNextPage || isLoadingThreads"
                @click="goToPage(currentPage + 1)"
              >
                <span
                  class="i-lucide-chevron-right size-4"
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>
          <div
            class="flex min-h-12 shrink-0 items-center justify-between gap-2 border-b border-[#e0e3e7] px-3 md:hidden"
          >
            <div
              class="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto [&>*]:shrink-0"
            >
              <label
                class="inline-flex size-9 cursor-pointer items-center justify-center rounded-md hover:bg-[#f1f3f4]"
                :title="t('INBOX.EMAIL.SELECT_ALL')"
                ><input
                  type="checkbox"
                  class="size-4 cursor-pointer accent-[#15603e]"
                  :checked="allVisibleSelected"
                  :indeterminate="someVisibleSelected"
                  :disabled="filters.folder === 'drafts' || !threads.length"
                  :aria-label="t('INBOX.EMAIL.SELECT_ALL')"
                  @change="toggleSelectAll" /></label
              ><button
                type="button"
                class="inline-flex size-9 items-center justify-center rounded-full text-[#5f6368] hover:bg-[#f1f3f4]"
                :aria-label="t('INBOX.EMAIL.REFRESH')"
                :disabled="isLoadingThreads || isBulkActionLoading"
                @click="refreshList"
              >
                <span
                  class="i-lucide-refresh-cw size-[18px]"
                  aria-hidden="true"
                /></button
              ><button
                v-if="selectedCount"
                type="button"
                class="inline-flex size-9 items-center justify-center rounded-full text-[#5f6368] hover:bg-[#f1f3f4]"
                :aria-label="t('INBOX.EMAIL.MARK_READ')"
                @click="markSelectedRead"
              >
                <span
                  class="i-lucide-mail-open size-[18px]"
                  aria-hidden="true"
                /></button
              ><button
                v-if="selectedCount"
                type="button"
                class="inline-flex size-9 items-center justify-center rounded-full text-[#5f6368] hover:bg-[#f1f3f4]"
                :aria-label="t('INBOX.EMAIL.MARK_UNREAD')"
                @click="markSelectedUnread"
              >
                <span class="i-lucide-mail size-[18px]" aria-hidden="true" />
              </button>
              <button
                v-if="selectedCount"
                type="button"
                class="inline-flex size-9 items-center justify-center rounded-full text-[#5f6368]"
                :aria-label="t('INBOX.EMAIL.SNOOZE')"
                @click="openSnooze"
              >
                <span class="i-lucide-clock-3 size-[18px]" />
              </button>
              <button
                v-if="selectedCount"
                type="button"
                class="inline-flex size-9 items-center justify-center rounded-full text-[#5f6368]"
                :aria-label="t('INBOX.EMAIL.LABELS')"
                @click="openLabels"
              >
                <LabelTagIcon :size="18" />
              </button>
              <button
                v-for="action in ['trash', 'spam', 'archived'].includes(
                  filters.folder
                )
                  ? [
                      {
                        key: 'inbox',
                        icon: 'i-lucide-inbox',
                        label: t('INBOX.EMAIL.RESTORE'),
                      },
                    ]
                  : [
                      {
                        key: 'archived',
                        icon: 'i-lucide-archive',
                        label: t('INBOX.EMAIL.ARCHIVE'),
                      },
                      {
                        key: 'spam',
                        icon: 'i-lucide-shield-alert',
                        label: t('INBOX.EMAIL.MARK_SPAM'),
                      },
                      {
                        key: 'trash',
                        icon: 'i-lucide-trash-2',
                        label: t('INBOX.EMAIL.MOVE_TRASH'),
                      },
                    ]"
                :key="action.key"
                type="button"
                class="inline-flex size-9 items-center justify-center rounded-full text-[#5f6368]"
                :aria-label="action.label"
                :title="action.label"
                @click="moveSelected(action.key)"
              >
                <span :class="action.icon" class="size-[18px]" />
              </button>
            </div>
            <div class="flex items-center gap-1 text-[11px] text-[#5f6368]">
              <span
                >{{ rangeStart }}–{{ rangeEnd }} {{ t('INBOX.EMAIL.OF') }}
                {{ totalCount }}</span
              ><button
                type="button"
                class="inline-flex size-8 items-center justify-center rounded-full hover:bg-[#f1f3f4] disabled:opacity-30"
                :aria-label="t('INBOX.EMAIL.PREVIOUS_PAGE')"
                :disabled="!hasPreviousPage || isLoadingThreads"
                @click="goToPage(currentPage - 1)"
              >
                <span
                  class="i-lucide-chevron-left size-4"
                  aria-hidden="true"
                /></button
              ><button
                type="button"
                class="inline-flex size-8 items-center justify-center rounded-full hover:bg-[#f1f3f4] disabled:opacity-30"
                :aria-label="t('INBOX.EMAIL.NEXT_PAGE')"
                :disabled="!hasNextPage || isLoadingThreads"
                @click="goToPage(currentPage + 1)"
              >
                <span
                  class="i-lucide-chevron-right size-4"
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>
          <div
            v-if="filters.folder === 'inbox'"
            class="flex shrink-0 items-center gap-1 overflow-x-auto border-b border-[#e0e3e7] px-2 md:px-3"
            role="tablist"
            :aria-label="t('INBOX.EMAIL.CATEGORIES')"
          >
            <button
              v-for="category in categories"
              :key="category.key"
              type="button"
              role="tab"
              class="flex min-w-32 shrink-0 items-center gap-2 border-b-[3px] px-3 py-3 text-left text-xs font-medium transition-colors md:min-w-40 md:px-4 md:text-[13px]"
              :class="
                filters.category === category.key
                  ? 'border-[#16834b] text-[#15603e]'
                  : 'border-transparent text-[#5f6368] hover:bg-[#f5faf6] hover:text-[#202124]'
              "
              :aria-selected="filters.category === category.key"
              @click="setCategory(category.key)"
            >
              <span
                class="size-4 shrink-0"
                :class="[category.icon]"
                aria-hidden="true"
              /><span class="truncate">{{ category.label }}</span>
            </button>
          </div>
          <div
            class="flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden"
          >
            <div
              v-if="hasNoInboxes"
              class="flex flex-1 flex-col items-center justify-center gap-2 px-6 py-10 text-center"
            >
              <span
                class="i-lucide-mail size-9 text-[#80868b]"
                aria-hidden="true"
              />
              <p class="text-sm font-semibold text-[#202124]">
                {{ t('INBOX.EMAIL.NO_ACCOUNTS_TITLE') }}
              </p>
              <p class="max-w-sm text-[13px] text-[#5f6368]">
                {{ t('INBOX.EMAIL.NO_ACCOUNTS_DESCRIPTION') }}
              </p>
            </div>
            <template v-else>
              <EmailListItem
                v-for="thread in threads"
                :key="thread.id"
                :thread="thread"
                :active="activeThreadId === thread.id"
                :selected="selectedThreadIds.has(thread.id)"
                :show-account="hasManyInboxes && !filters.inboxId"
                :available-labels="labels"
                @open="openThread"
                @toggle-select="toggleThreadSelected"
                @toggle-star="toggleStar"
              />
              <div v-if="isLoadingThreads" class="flex justify-center py-8">
                <Spinner class="text-n-brand" />
              </div>
              <div
                v-if="loadError"
                class="flex flex-col items-center gap-2 px-6 py-8 text-center"
              >
                <p class="text-[13px] text-[#5f6368]">
                  {{ t('INBOX.EMAIL.LOAD_ERROR') }}
                </p>
                <button
                  type="button"
                  class="text-[13px] font-semibold text-[#15603e]"
                  @click="fetchThreads({ page: currentPage })"
                >
                  {{ t('INBOX.EMAIL.RETRY') }}
                </button>
              </div>
              <EmailInboxEmptyState
                v-if="showEmptyState"
                :filtered="hasActiveFilter"
              />
            </template>
          </div>
        </template>
      </main>
    </div>

    <div
      v-if="composeOpen"
      class="fixed inset-0 z-[100] flex items-end justify-center bg-black/30 p-0 md:items-center md:p-6"
      @click.self="closeCompose"
    >
      <form
        class="flex max-h-full w-full flex-col overflow-hidden bg-white shadow-2xl md:max-w-xl md:rounded-xl"
        @submit.prevent="sendCompose"
      >
        <header
          class="flex items-center justify-between bg-[#f3f8f4] px-4 py-3"
        >
          <h2 class="text-sm font-semibold text-[#202124]">
            {{ t('INBOX.EMAIL.COMPOSE') }}
          </h2>
          <button
            type="button"
            class="inline-flex size-8 items-center justify-center rounded-full text-[#5f6368] hover:bg-[#e4e9f0]"
            :aria-label="t('INBOX.EMAIL.CLOSE_COMPOSE')"
            @click="closeCompose"
          >
            <span class="i-lucide-x size-4" aria-hidden="true" />
          </button>
        </header>
        <div class="flex flex-col gap-3 overflow-y-auto p-4">
          <div class="relative min-w-0">
            <select
              v-model="composeForm.inboxId"
              class="h-10 w-full min-w-0 appearance-none truncate rounded-lg border border-[#dadce0] bg-white pl-3 pr-9 text-sm text-[#3c4043] outline-none focus:border-[#16834b]"
              required
            >
              <option disabled value="">
                {{ t('INBOX.EMAIL.COMPOSE_ACCOUNT') }}
              </option>
              <option
                v-for="inbox in inboxes"
                :key="inbox.id"
                :value="inbox.id"
              >
                {{ inbox.email }}
              </option>
            </select>
            <span
              class="i-lucide-chevron-down pointer-events-none absolute right-3 top-3 size-4 text-[#5f6368]"
              aria-hidden="true"
            />
          </div>
          <EmailRecipientChips
            ref="toRecipients"
            v-model="composeForm.to"
            :label="t('INBOX.EMAIL.COMPOSE_TO')"
            :placeholder="t('INBOX.EMAIL.COMPOSE_TO_MULTIPLE')"
          />
          <button
            v-if="!showCopyRecipients"
            type="button"
            class="self-start text-xs font-medium text-[#15603e]"
            @click="showCopyRecipients = true"
          >
            {{ t('INBOX.EMAIL.ADD_CC') }}
          </button>
          <template v-if="showCopyRecipients">
            <EmailRecipientChips
              ref="ccRecipients"
              v-model="composeForm.cc"
              :label="t('INBOX.EMAIL.CC')"
              :placeholder="t('INBOX.EMAIL.CC_PLACEHOLDER')"
            />
            <EmailRecipientChips
              ref="bccRecipients"
              v-model="composeForm.bcc"
              :label="t('INBOX.EMAIL.BCC')"
              :placeholder="t('INBOX.EMAIL.BCC_PLACEHOLDER')"
            />
          </template>
          <input
            v-model="composeForm.subject"
            type="text"
            class="h-10 rounded-lg border border-[#dadce0] px-3 text-sm text-[#202124] outline-none placeholder:text-[#80868b] focus:border-[#16834b]"
            :placeholder="t('INBOX.EMAIL.COMPOSE_SUBJECT')"
            required
          /><textarea
            v-model="composeForm.content"
            rows="9"
            class="min-h-40 resize-y rounded-lg border border-[#dadce0] px-3 py-2 text-sm text-[#202124] outline-none placeholder:text-[#80868b] focus:border-[#16834b]"
            :placeholder="t('INBOX.EMAIL.COMPOSE_MESSAGE')"
            required
          />
          <label
            class="inline-flex cursor-pointer items-center gap-2 text-xs font-medium text-[#15603e]"
          >
            <span class="i-lucide-paperclip size-4" aria-hidden="true" />{{
              t('INBOX.EMAIL.ADD_ATTACHMENT')
            }}
            <input
              type="file"
              multiple
              class="sr-only"
              @change="addDraftFiles"
            />
          </label>
          <div
            v-if="draftFiles.length || pendingFiles.length"
            class="flex flex-wrap gap-2"
          >
            <span
              v-for="file in draftFiles"
              :key="file.id"
              class="inline-flex items-center gap-1 rounded-full bg-[#e6f4ea] px-3 py-1 text-xs text-[#15603e]"
            >
              {{ file.name }}
              <button
                type="button"
                :aria-label="t('INBOX.EMAIL.REMOVE_ATTACHMENT')"
                @click="removeDraftFile(file)"
              >
                <span class="i-lucide-x size-3" aria-hidden="true" />
              </button>
            </span>
            <span
              v-for="file in pendingFiles"
              :key="file.name"
              class="rounded-full bg-[#e6f4ea] px-3 py-1 text-xs text-[#15603e]"
              >{{ file.name }}</span
            >
          </div>
        </div>
        <footer
          class="flex items-center justify-end gap-2 border-t border-[#e0e3e7] px-4 py-3"
        >
          <span v-if="isSavingDraft" class="mr-auto text-xs text-[#5f6368]">{{
            t('INBOX.EMAIL.SAVING_DRAFT')
          }}</span>
          <button
            v-if="draftId"
            type="button"
            class="mr-auto text-xs text-[#5f6368] hover:text-red-600"
            @click="discardDraft"
          >
            {{ t('INBOX.EMAIL.DISCARD_DRAFT') }}
          </button>
          <button
            type="button"
            class="rounded-full px-4 py-2 text-sm text-[#5f6368] hover:bg-[#f1f3f4]"
            @click="closeCompose"
          >
            {{ t('INBOX.EMAIL.CANCEL') }}</button
          ><button
            type="submit"
            class="rounded-full bg-[#15603e] px-5 py-2 text-sm font-semibold text-white hover:bg-[#104b31] disabled:opacity-50"
            :disabled="isComposing"
          >
            {{ isComposing ? t('INBOX.EMAIL.SENDING') : t('INBOX.EMAIL.SEND') }}
          </button>
        </footer>
      </form>
    </div>
    <EmailSnoozeDialog
      :open="snoozeOpen"
      :anchor-rect="snoozeAnchor"
      @close="snoozeOpen = false"
      @confirm="snoozeSelected"
    />
    <div
      v-if="labelOpen"
      class="fixed inset-0 z-[101] flex items-center justify-center bg-black/30 p-4"
      @click.self="labelOpen = false"
    >
      <div class="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl">
        <h2 class="text-base font-semibold text-[#202124]">
          {{ t('INBOX.EMAIL.LABELS') }}
        </h2>
        <div
          v-if="selectedCount"
          class="mt-3 max-h-56 space-y-2 overflow-y-auto"
        >
          <label
            v-for="label in labels"
            :key="label.id"
            class="flex items-center gap-2 text-sm text-[#3c4043]"
          >
            <input
              v-model="labelSelection"
              type="checkbox"
              :value="label.title"
              :disabled="
                selectedCount === 1 &&
                threads
                  .find(item => selectedThreadIds.has(item.id))
                  ?.inherited_labels?.includes(label.title)
              "
              class="accent-[#15603e]"
            />
            <LabelTagIcon :color="label.color" :size="14" />{{ label.title }}
          </label>
        </div>
        <form
          class="mt-4 flex items-center gap-2"
          @submit.prevent="createLabel"
        >
          <input
            v-model="labelName"
            type="text"
            class="h-10 min-w-0 flex-1 rounded-lg border border-[#dadce0] px-3 text-sm"
            :placeholder="t('INBOX.EMAIL.ADD_LABEL')"
          />
          <input
            v-model="labelColor"
            type="color"
            class="size-9"
            :aria-label="t('INBOX.EMAIL.LABEL_COLOR')"
          />
          <button
            type="submit"
            class="rounded-full bg-[#e6f4ea] px-3 py-2 text-xs font-semibold text-[#15603e]"
          >
            {{ t('INBOX.EMAIL.CREATE_LABEL') }}
          </button>
        </form>
        <div class="mt-5 flex justify-end gap-2">
          <button
            type="button"
            class="rounded-full px-4 py-2 text-sm text-[#5f6368]"
            @click="labelOpen = false"
          >
            {{ t('INBOX.EMAIL.CANCEL') }}
          </button>
          <button
            v-if="selectedCount"
            type="button"
            class="rounded-full bg-[#15603e] px-4 py-2 text-sm font-semibold text-white"
            @click="saveLabels"
          >
            {{ t('INBOX.EMAIL.APPLY_LABELS') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
